// Decide qué eventos de Sentry se descartan y cómo se agrupan los que sí se
// envían. Módulo puro — sin React, sin Zustand y **sin importar Sentry**: sólo
// recibe el objeto del evento y devuelve el evento (posiblemente modificado) o
// `null` para descartarlo. Misma prohibición de mockearlo en los tests de sus
// consumidores que `oneclickCards.js` / `paymentGuard.js`.
//
// El problema que resuelve: `ApiCore.js` reporta TODO fallo de fetch con
// `captureException` desde 14 call sites distintos, y Sentry agrupa por frame.
// Resultado real en el dashboard: ~17 issues separadas que son el mismo
// fenómeno ("TypeError: Failed to fetch" / "Load failed"), sumando decenas de
// usuarios, que enterraban las incidencias de verdad — la de inscripción
// OneClick bloqueada en producción estaba en el puesto 6 por volumen.
//
// Criterio: NO se silencia un backend caído. Sólo se descarta lo que
// demostrablemente no es un fallo nuestro (el navegador sin conexión, o código
// de una extensión del navegador); el resto se envía igual, pero agrupado.

// Mensajes con los que los distintos navegadores reportan un fetch que no
// llegó a completarse. Safari/iOS dice "Load failed", Chrome/Firefox "Failed
// to fetch"; `NetworkError` aparece en Firefox antiguo.
const MENSAJES_FETCH_CAIDO = [
  "failed to fetch",
  "load failed",
  "networkerror when attempting to fetch resource",
  "network request failed",
];

// Prefijos de código que no es nuestro. `ext:` es el que produjo el evento
// real `TypeError: e.assignedElements is not a function` (culprit
// `ext:core/01_core.js`), que viene de una extensión instalada por el usuario.
const ORIGENES_EXTENSION = [
  "chrome-extension://",
  "moz-extension://",
  "safari-web-extension://",
  "safari-extension://",
  "ext:",
];

const textoDe = (valor) => (typeof valor === "string" ? valor : "");

/** Primera excepción del evento, si la hay. */
const excepcionDe = (event) => event?.exception?.values?.[0] ?? null;

/** Mensaje legible del evento, venga de `message` o de la excepción. */
export const mensajeDelEvento = (event) => {
  const exc = excepcionDe(event);
  return textoDe(exc?.value) || textoDe(event?.message);
};

/**
 * esFetchCaido(event)
 * `true` si el evento es un fetch que no llegó a completarse — la red se cayó,
 * el usuario cerró la pestaña a mitad de request, o el sistema suspendió la
 * app. No distingue la causa: para eso está `navigator.onLine`.
 */
export const esFetchCaido = (event) => {
  const mensaje = mensajeDelEvento(event).toLowerCase();
  if (!mensaje) return false;
  return MENSAJES_FETCH_CAIDO.some((m) => mensaje.includes(m));
};

/**
 * vieneDeExtension(event)
 * `true` si alguno de los frames del stack pertenece a una extensión del
 * navegador. Esos errores los produce software que el usuario instaló, no
 * esta aplicación, y no hay nada que arreglar de nuestro lado.
 */
export const vieneDeExtension = (event) => {
  const frames = excepcionDe(event)?.stacktrace?.frames;
  const candidatos = Array.isArray(frames)
    ? frames.map((f) => `${textoDe(f?.filename)} ${textoDe(f?.abs_path)}`)
    : [];
  candidatos.push(textoDe(event?.culprit));
  return candidatos.some((texto) => {
    const t = texto.toLowerCase();
    return ORIGENES_EXTENSION.some((origen) => t.includes(origen));
  });
};

/**
 * filtrarEventoSentry(event, { online })
 * @param online `navigator.onLine` en el momento del evento. Se inyecta en vez
 *   de leerse acá para que el módulo siga siendo puro y testeable. Si no se
 *   sabe (undefined), se asume con conexión: ante la duda se envía.
 * @returns el evento a enviar (con `fingerprint` si corresponde), o `null`
 *   para descartarlo.
 *
 * Nunca lanza: si algo acá explotara, Sentry se quedaría ciego.
 */
export const filtrarEventoSentry = (event, { online } = {}) => {
  try {
    if (!event) return event ?? null;

    if (vieneDeExtension(event)) return null;

    if (esFetchCaido(event)) {
      // Sin conexión no es un fallo de la aplicación: el navegador no llegó a
      // mandar la request. Se descarta.
      if (online === false) return null;

      // Con conexión sí se envía — podría ser el backend caído, CORS o un
      // timeout real — pero agrupado en UNA issue en vez de una por cada
      // método de ApiCore desde el que se reportó.
      const metodo = textoDe(event?.extra?.method).toUpperCase() || "DESCONOCIDO";
      return { ...event, fingerprint: ["red-fetch-fallido", metodo] };
    }

    return event;
  } catch {
    return event ?? null;
  }
};
